import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const COLUMNS = [
  { id: 'todo', label: 'To Do', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  { id: 'done', label: 'Done', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' }
];

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' }
];

const isOverdue = (task) => {
  if (!task?.due_date || task?.status === 'done') return false;
  return new Date(task.due_date) < new Date();
};

const TaskCard = ({ task, index, onDelete, onEdit }) => {
  const priorityCfg = PRIORITY_CONFIG?.[task?.priority] || PRIORITY_CONFIG?.medium;
  const overdue = isOverdue(task);

  return (
    <Draggable draggableId={task?.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided?.innerRef}
          {...provided?.draggableProps}
          {...provided?.dragHandleProps}
          className={`bg-card border rounded-lg p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
            overdue ? 'border-red-300 bg-red-50/50' : 'border-border'
          } ${snapshot?.isDragging ? 'shadow-lg ring-2 ring-primary/30' : 'hover:shadow-md'}`}
        >
          <div className="flex items-start justify-between mb-2">
            <p
              className={`text-sm font-medium flex-1 mr-2 cursor-pointer hover:text-primary transition-colors ${overdue ? 'text-red-700' : 'text-foreground'}`}
              onClick={(e) => { e?.stopPropagation(); onEdit(task); }}
            >
              {task?.title}
            </p>
            <div className="flex items-center space-x-1 flex-shrink-0">
              <button
                onClick={(e) => { e?.stopPropagation(); onEdit(task); }}
                className="p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-colors"
                title="Edit task"
              >
                <Icon name="Pencil" size={12} />
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); onDelete(task?.id); }}
                className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Delete task"
              >
                <Icon name="Trash2" size={12} />
              </button>
            </div>
          </div>
          {task?.description && (
            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{task?.description}</p>
          )}
          <div className="flex items-center justify-between">
            <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${priorityCfg?.color}`}>
              {priorityCfg?.label}
            </span>
            <div className="flex items-center space-x-2">
              {task?.due_date && (
                <span className={`text-xs flex items-center ${
                  overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
                }`}>
                  <Icon name="Clock" size={10} className="mr-1" />
                  {task?.due_date}
                  {overdue && ' ⚠'}
                </span>
              )}
              {task?.assigned?.full_name && (
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center" title={task?.assigned?.full_name}>
                  <span className="text-xs text-primary-foreground font-medium">{task?.assigned?.full_name?.[0]}</span>
                </div>
              )}
            </div>
          </div>
          {task?.related_to_type && (
            <div className="mt-2">
              <span className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded-full">
                {task?.related_to_type}
              </span>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

/* ─── Edit Task Drawer ─── */
const EditTaskDrawer = ({ task, teamMembers, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'medium',
    status: task?.status || 'todo',
    due_date: task?.due_date || '',
    assigned_to: task?.assigned_to || '',
    related_to_type: task?.related_to_type || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form?.title?.trim()) {
      toast?.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase?.from('tasks')?.update({
          title: form?.title?.trim(),
          description: form?.description || null,
          priority: form?.priority,
          status: form?.status,
          due_date: form?.due_date || null,
          assigned_to: form?.assigned_to || null,
          related_to_type: form?.related_to_type || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', task?.id);
      if (error) throw error;
      toast?.success('Task updated!');
      onSaved();
      onClose();
    } catch (err) {
      toast?.error(err?.message || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />
      {/* Drawer */}
      <div className="w-full max-w-md bg-card border-l border-border flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Edit Task</h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form?.title}
              onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              placeholder="Task title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={form?.description}
              onChange={e => setForm(p => ({ ...p, description: e?.target?.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
              placeholder="Add a description..."
            />
          </div>

          {/* Status & Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <select
                value={form?.status}
                onChange={e => setForm(p => ({ ...p, status: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                {STATUS_OPTIONS?.map(s => (
                  <option key={s?.value} value={s?.value}>{s?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
              <select
                value={form?.priority}
                onChange={e => setForm(p => ({ ...p, priority: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Due Date</label>
            <input
              type="date"
              value={form?.due_date}
              onChange={e => setForm(p => ({ ...p, due_date: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            />
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Assigned To</label>
            <select
              value={form?.assigned_to}
              onChange={e => setForm(p => ({ ...p, assigned_to: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            >
              <option value="">Unassigned</option>
              {teamMembers?.map(m => (
                <option key={m?.id} value={m?.id}>{m?.full_name}</option>
              ))}
            </select>
          </div>

          {/* Related To */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Related To</label>
            <select
              value={form?.related_to_type}
              onChange={e => setForm(p => ({ ...p, related_to_type: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            >
              <option value="">None</option>
              <option value="job">Job</option>
              <option value="lead">Lead</option>
              <option value="contact">Contact</option>
              <option value="deal">Deal</option>
              <option value="account">Account</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex space-x-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={saving || !form?.title?.trim()}>
            {saving ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Icon name="Save" size={14} className="mr-2" />
                Save Changes
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const TasksPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '', description: '', priority: 'medium', due_date: '',
    assigned_to: '', related_to_type: ''
  });
  const { profile } = useAuth();

  const fetchTasks = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('tasks')?.select('*, assigned:profiles(full_name)')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false });
      if (error) throw error;
      setTasks(data || []);
    } catch (err) {
      toast?.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  const fetchTeam = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('profiles')?.select('id, full_name')?.eq('org_id', profile?.org_id);
    if (data) setTeamMembers(data);
  }, [profile?.org_id]);

  useEffect(() => { fetchTasks(); fetchTeam(); }, [fetchTasks, fetchTeam]);

  const handleAddTask = async () => {
    if (!newTask?.title?.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase?.from('tasks')?.insert({
        ...newTask,
        org_id: profile?.org_id,
        status: 'todo',
        assigned_to: newTask?.assigned_to || null,
        due_date: newTask?.due_date || null,
        related_to_type: newTask?.related_to_type || null
      });
      if (error) throw error;
      toast?.success('Task added!');
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', assigned_to: '', related_to_type: '' });
      setIsAddModalOpen(false);
      fetchTasks();
    } catch (err) {
      toast?.error(err?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await supabase?.from('tasks')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', taskId);
      toast?.success('Moved to Trash');
      fetchTasks();
    } catch (err) {
      toast?.error('Failed to delete');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result?.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination?.droppableId;
    setTasks(prev => prev?.map(t => t?.id === draggableId ? { ...t, status: newStatus } : t));
    try {
      await supabase?.from('tasks')?.update({ status: newStatus })?.eq('id', draggableId);
    } catch (err) {
      toast?.error('Failed to update status');
      fetchTasks();
    }
  };

  const getColumnTasks = (status) => tasks?.filter(t => t?.status === status);
  const overdueCount = tasks?.filter(isOverdue)?.length;

  return (
    <>
      <Helmet><title>Tasks - Wilcart CRM</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Tasks</h1>
                <div className="flex items-center space-x-3 mt-1">
                  <p className="text-muted-foreground">{tasks?.length} total tasks</p>
                  {overdueCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                      {overdueCount} overdue
                    </span>
                  )}
                </div>
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="mt-4 sm:mt-0">
                <Icon name="Plus" size={16} className="mr-2" />
                Add Task
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex space-x-4 overflow-x-auto pb-4">
                  {COLUMNS?.map(col => (
                    <div key={col?.id} className="flex-shrink-0 w-72">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className={`w-2 h-2 rounded-full ${col?.dot}`} />
                        <span className="text-sm font-medium text-foreground">{col?.label}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${col?.color}`}>
                          {getColumnTasks(col?.id)?.length}
                        </span>
                      </div>
                      <Droppable droppableId={col?.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided?.innerRef}
                            {...provided?.droppableProps}
                            className={`min-h-32 rounded-xl p-2 transition-colors ${
                              snapshot?.isDraggingOver ? 'bg-primary/5 border-2 border-dashed border-primary/30' : 'bg-muted/30'
                            }`}
                          >
                            {getColumnTasks(col?.id)?.map((task, index) => (
                              <TaskCard
                                key={task?.id}
                                task={task}
                                index={index}
                                onDelete={handleDeleteTask}
                                onEdit={setEditingTask}
                              />
                            ))}
                            {provided?.placeholder}
                            {getColumnTasks(col?.id)?.length === 0 && (
                              <div className="text-center py-8 text-xs text-muted-foreground">Drop tasks here</div>
                            )}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  ))}
                </div>
              </DragDropContext>
            )}
          </div>
        </main>

        {/* Add Task Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Add Task</h2>
                <button onClick={() => setIsAddModalOpen(false)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newTask?.title}
                    onChange={e => setNewTask(p => ({ ...p, title: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    placeholder="Task title"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea
                    value={newTask?.description}
                    onChange={e => setNewTask(p => ({ ...p, description: e?.target?.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                    <select
                      value={newTask?.priority}
                      onChange={e => setNewTask(p => ({ ...p, priority: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Due Date</label>
                    <input
                      type="date"
                      value={newTask?.due_date}
                      onChange={e => setNewTask(p => ({ ...p, due_date: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Assign To</label>
                    <select
                      value={newTask?.assigned_to}
                      onChange={e => setNewTask(p => ({ ...p, assigned_to: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    >
                      <option value="">Unassigned</option>
                      {teamMembers?.map(m => <option key={m?.id} value={m?.id}>{m?.full_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Related To</label>
                    <select
                      value={newTask?.related_to_type}
                      onChange={e => setNewTask(p => ({ ...p, related_to_type: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    >
                      <option value="">None</option>
                      <option value="job">Job</option>
                      <option value="lead">Lead</option>
                      <option value="contact">Contact</option>
                      <option value="deal">Deal</option>
                      <option value="account">Account</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button
                  className="flex-1"
                  onClick={handleAddTask}
                  disabled={saving || !newTask?.title?.trim()}
                >
                  {saving ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Adding...
                    </span>
                  ) : 'Add Task'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Task Drawer */}
        {editingTask && (
          <EditTaskDrawer
            task={editingTask}
            teamMembers={teamMembers}
            onClose={() => setEditingTask(null)}
            onSaved={fetchTasks}
          />
        )}
      </div>
    </>
  );
};

export default TasksPage;
